import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { BRAND } from "@/lib/brand";

const DEFECTS = [
  ["Frame & Body", ["Frame–cracks, corrective welds, or rusted through", "Dog tracks–bent or twisted frame"]],
  ["Engine", ["Oil leakage, excluding normal seepage", "Cracked block or head", "Belts missing or inoperable", "Knocks or misses related to camshaft, lifters and push rods", "Abnormal exhaust discharge"]],
  ["Transmission & Drive Shaft", ["Improper fluid level or leakage, excluding normal seepage", "Cracked or damaged case which is visible", "Abnormal noise or vibration caused by faulty transmission or drive shaft", "Improper shifting or functioning in any gear", "Manual clutch slips or chatters"]],
  ["Differential", ["Improper fluid level or leakage, excluding normal seepage", "Cracked or damaged housing which is visible", "Abnormal noise or vibration caused by faulty differential"]],
  ["Cooling System", ["Leakage including radiator", "Improperly functioning water pump"]],
  ["Electrical System", ["Battery leakage", "Improperly functioning alternator, generator, battery, or starter"]],
  ["Fuel System", ["Visible leakage"]],
  ["Inoperable Accessories", ["Gauges or warning devices", "Air conditioner", "Heater & defroster"]],
  ["Brake System", ["Failure warning light broken", "Pedal not firm under pressure (DOT spec.)", "Not enough pedal reserve (DOT spec.)", "Does not stop vehicle in straight line (DOT spec.)", "Hoses damaged", "Drum or rotor too thin (Mfgr. specs.)", "Lining or pad thickness less than 1/32 inch", "Power unit not operating or leaking", "Structural or mechanical parts damaged"]],
  ["Air Bags", []],
  ["Steering System", ["Too much free play at steering wheel (DOT specs.)", "Free play in linkage more than 1/4 inch", "Steering gear binds or jams", "Front wheels aligned improperly (DOT specs.)", "Power unit belts cracked or slipping", "Power unit fluid level improper"]],
  ["Suspension System", ["Ball joint seals damaged", "Structural parts bent or damaged", "Stabilizer bar disconnected", "Spring broken", "Shock absorber mounting loose", "Rubber bushings damaged or missing", "Radius rod damaged or missing", "Shock absorber leaking or functioning improperly"]],
  ["Tires", ["Tread depth less than 2/32 inch", "Sizes mismatched", "Visible damage"]],
  ["Wheels", ["Visible cracks, damage or repairs", "Mounting bolts loose or missing"]],
  ["Exhaust System", ["Leakage", "Catalytic Converter"]],
];

const Blank = ({ w = "8rem" }) => (
  <span className="inline-block border-b border-black align-bottom" style={{ width: w }}>&nbsp;</span>
);

export default function BuyersGuide() {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [error, setError] = useState(false);
  const [asIs, setAsIs] = useState(true);

  useEffect(() => {
    api.get(`/cars/${id}`).then((r) => setCar(r.data)).catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Vehicle not found. <Link to="/admin" className="ml-2 underline">Back to admin</Link>
      </div>
    );
  }
  if (!car) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-secondary/60 print:bg-white" data-testid="buyers-guide-page">
      <style>{`
        @page { size: letter; margin: 0.45in; }
        @media print {
          .no-print { display: none !important; }
          .bg-sheet { box-shadow: none !important; margin: 0 auto !important; border: none !important; }
          .bg-page-break { page-break-before: always; }
        }
        .bg-sheet input[type="checkbox"] { width: 14px; height: 14px; accent-color: black; }
      `}</style>

      <div className="no-print sticky top-0 z-10 bg-primary text-primary-foreground px-6 py-3 flex items-center justify-between">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm hover:underline" data-testid="bg-back">
          <ArrowLeft className="h-4 w-4" /> Back to admin
        </Link>
        <div className="flex items-center gap-3">
          <label className="text-sm flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={asIs} onChange={(e) => setAsIs(e.target.checked)} data-testid="bg-asis-toggle" />
            Sell &ldquo;AS IS&rdquo; (uncheck if offering a dealer warranty)
          </label>
          <Button onClick={() => window.print()} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-sm gap-2" data-testid="bg-print-btn">
            <Printer className="h-4 w-4" /> Print Buyers Guide
          </Button>
        </div>
      </div>

      {/* ============ FRONT ============ */}
      <div className="bg-sheet bg-white text-black max-w-[8.5in] mx-auto my-8 p-10 shadow-xl border border-black/10 text-[13px] leading-snug">
        <h1 className="text-center text-5xl font-black tracking-wide">BUYERS GUIDE</h1>
        <p className="mt-3 font-bold">
          IMPORTANT: Spoken promises are difficult to enforce. Ask the dealer to put all promises in writing. Keep this form.
        </p>

        <div className="mt-4 grid grid-cols-4 gap-3 border-2 border-black p-3" data-testid="bg-vehicle-info">
          <div><p className="text-[10px] uppercase font-bold">Vehicle Make</p><p className="font-mono border-b border-black pb-0.5">{car.make}</p></div>
          <div><p className="text-[10px] uppercase font-bold">Model</p><p className="font-mono border-b border-black pb-0.5">{car.model}</p></div>
          <div><p className="text-[10px] uppercase font-bold">Year</p><p className="font-mono border-b border-black pb-0.5">{car.year}</p></div>
          <div><p className="text-[10px] uppercase font-bold">VIN Number</p><p className="font-mono border-b border-black pb-0.5 text-[11px]">{car.vin || "\u00A0"}</p></div>
        </div>
        <p className="mt-1 text-[10px]">DEALER STOCK NUMBER (Optional): <span className="font-mono">{car.external_id || ""}</span></p>

        <p className="mt-4 text-xl font-black uppercase">Warranties for this vehicle:</p>

        <div className={`mt-2 border-2 border-black p-3 ${asIs ? "" : "opacity-60"}`}>
          <label className="flex items-start gap-2 font-black text-2xl uppercase">
            <input type="checkbox" checked={asIs} onChange={(e) => setAsIs(e.target.checked)} className="mt-2" data-testid="bg-asis-box" />
            <span>As Is &ndash; No Dealer Warranty</span>
          </label>
          <p className="mt-1 font-bold uppercase">The dealer does not provide a warranty for any repairs after sale.</p>
        </div>

        <div className={`mt-3 border-2 border-black p-3 ${asIs ? "opacity-60" : ""}`}>
          <label className="flex items-start gap-2 font-black text-2xl uppercase">
            <input type="checkbox" checked={!asIs} onChange={(e) => setAsIs(!e.target.checked)} className="mt-2" data-testid="bg-warranty-box" />
            <span>Dealer Warranty</span>
          </label>
          <div className="mt-2 space-y-1.5">
            <label className="flex items-center gap-2 font-bold uppercase"><input type="checkbox" /> Full Warranty</label>
            <label className="flex items-center gap-2 font-bold uppercase"><input type="checkbox" /> Limited Warranty</label>
            <p>
              The dealer will pay <Blank w="3rem" />% of the labor and <Blank w="3rem" />% of the parts for the covered systems that fail
              during the warranty period. Ask the dealer for a copy of the warranty document for a full explanation of warranty coverage,
              exclusions, and the dealer&rsquo;s repair obligations. Implied warranties under your state&rsquo;s laws may give you additional rights.
            </p>
            <p className="font-bold uppercase pt-1">Systems Covered: <Blank w="20rem" /></p>
            <p className="font-bold uppercase">Duration: <Blank w="24rem" /></p>
          </div>
        </div>

        <div className="mt-3 border-2 border-black p-3">
          <p className="font-black uppercase">Non-Dealer Warranties for this Vehicle:</p>
          <div className="mt-1.5 space-y-1">
            <label className="flex items-start gap-2"><input type="checkbox" className="mt-0.5" /> <span><span className="font-bold uppercase">Manufacturer&rsquo;s Warranty Still Applies.</span> The manufacturer&rsquo;s original warranty has not expired on some components of the vehicle.</span></label>
            <label className="flex items-start gap-2"><input type="checkbox" className="mt-0.5" /> <span className="font-bold uppercase">Manufacturer&rsquo;s Used Vehicle Warranty Applies.</span></label>
            <label className="flex items-start gap-2"><input type="checkbox" className="mt-0.5" /> <span className="font-bold uppercase">Other Used Vehicle Warranty Applies.</span></label>
          </div>
          <p className="mt-1.5">Ask the dealer for a copy of the warranty document and an explanation of warranty coverage, exclusions, and repair obligations.</p>
        </div>

        <div className="mt-3 border-2 border-black p-3">
          <label className="flex items-start gap-2">
            <input type="checkbox" className="mt-0.5" />
            <span>
              <span className="font-black uppercase">Service Contract.</span> A service contract on this vehicle is available for an extra charge.
              Ask for details about coverage, deductible, price, and exclusions. If you buy a service contract within 90 days of your purchase
              of this vehicle, implied warranties under your state&rsquo;s laws may give you additional rights.
            </span>
          </label>
        </div>

        <p className="mt-4 font-black uppercase">Ask the dealer if your mechanic can inspect the vehicle on or off the lot.</p>
        <p className="mt-2 font-bold">
          SEE THE BACK OF THIS FORM for important additional information, including a list of some major defects that may occur in used motor vehicles.
        </p>
      </div>

      {/* ============ BACK ============ */}
      <div className="bg-sheet bg-page-break bg-white text-black max-w-[8.5in] mx-auto my-8 p-10 shadow-xl border border-black/10 text-[11px] leading-snug">
        <p className="font-bold text-[13px]">Here is a list of some major defects that may occur in used motor vehicles.</p>
        <div className="mt-3 columns-2 gap-8" data-testid="bg-defects">
          {DEFECTS.map(([system, items]) => (
            <div key={system} className="break-inside-avoid mb-2.5">
              <p className="font-black uppercase border-b border-black">{system}</p>
              <ul className="mt-0.5 list-disc pl-4 space-y-0">
                {items.map((it, i) => (<li key={i}>{it}</li>))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-5 border-2 border-black p-3 text-[13px]" data-testid="bg-dealer-info">
          <p className="font-black uppercase">Dealer</p>
          <p className="font-semibold">{BRAND.name} · {BRAND.dealerNumber}</p>
          <p>{BRAND.addressLine1}, {BRAND.addressLine2}</p>
          <p className="mt-2 font-black uppercase">See for Complaints</p>
          <p>Kendall — {BRAND.phone} · {BRAND.email}</p>
        </div>

        <p className="mt-4 font-bold text-[13px]">
          IMPORTANT: The information on this form is part of any contract to buy this vehicle. Removing this label before consumer
          purchase (except for purpose of test-driving) violates federal law (16 C.F.R. 455).
        </p>
      </div>
    </div>
  );
}
